import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useState, useRef, useEffect } from 'react';
import { useChatHistory } from '../../hooks/useChatHistory';
import { useVaults } from '../../hooks/useVaults';
import { VaultAccessModal } from '../VaultAccessModal';

const WORKER_URL = import.meta.env.VITE_API_BASE ?? window.location.origin;

export function Layout() {
  const { tenant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { vaults, activeVaultId, setActiveVaultId, createVault, updateVault, deleteVault } = useVaults();
  const [vaultConfig, setVaultConfig] = useState<{ id: string; name: string } | null>(null);

  // Kho báu Quick Switcher state
  const [showVaultDropdown, setShowVaultDropdown] = useState(false);
  const [vaultSearch, setVaultSearch] = useState('');
  const vaultDropdownRef = useRef<HTMLDivElement>(null);

  // Chat section collapsible state
  const [showRecentChats, setShowRecentChats] = useState(true);

  const { 
    conversations, 
    activeId, 
    createConversation, 
    deleteConversation, 
    setActiveId 
  } = useChatHistory();

  // Close vault dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (vaultDropdownRef.current && !vaultDropdownRef.current.contains(e.target as Node)) {
        setShowVaultDropdown(false);
      }
    };
    if (showVaultDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVaultDropdown]);

  // Active Kho báu metadata
  const getActiveVaultInfo = () => {
    if (activeVaultId === 'shared') {
      return { name: 'Hộp thư nhận (Inbox)', type: 'shared', icon: '📥', typeLabel: 'Kho báu chia sẻ' };
    }
    if (!activeVaultId) {
      return { name: 'Không gian của tôi (Gốc)', type: 'personal', icon: '🏠', typeLabel: 'Kho báu cá nhân' };
    }
    const found = vaults.find(v => v.id === activeVaultId);
    if (!found) {
      return { name: 'Không gian của tôi (Gốc)', type: 'personal', icon: '🏠', typeLabel: 'Kho báu cá nhân' };
    }
    if (found.type === 'personal') {
      return { name: found.name, type: 'personal', icon: '👤', typeLabel: 'Kho báu cá nhân' };
    }
    if (found.type === 'shared') {
      return { name: found.name, type: 'shared', icon: '🤝', typeLabel: 'Kho báu chia sẻ' };
    }
    return { name: found.name, type: 'business', icon: '🏢', typeLabel: 'Kho báu doanh nghiệp' };
  };

  const activeVaultInfo = getActiveVaultInfo();

  // Filtered vaults for the switcher
  const term = vaultSearch.toLowerCase().trim();
  const personalVaults = vaults.filter(v => v.type === 'personal' && v.name.toLowerCase().includes(term));
  const sharedVaults = vaults.filter(v => v.type === 'shared' && v.name.toLowerCase().includes(term));
  const businessVaults = vaults.filter(v => (!v.type || v.type === 'business') && v.name.toLowerCase().includes(term));

  const handleQuickCreateVault = () => {
    const typeChoice = prompt('Chọn loại Kho báu muốn tạo:\n1. Kho báu Cá nhân (Nhập 1)\n2. Kho báu Doanh nghiệp / Nhóm (Nhập 2)', '1');
    if (!typeChoice) return;
    const isBusiness = typeChoice.trim() === '2';
    const type: 'personal' | 'business' = isBusiness ? 'business' : 'personal';
    const label = isBusiness ? 'Kho báu Doanh nghiệp / Nhóm' : 'Kho báu Cá nhân';
    const name = prompt(`Nhập tên cho ${label} mới:`);
    if (name && name.trim()) {
      createVault(name.trim(), type);
    }
  };

  const handleSelectVault = (id: string | null) => {
    setActiveVaultId(id);
    setShowVaultDropdown(false);
    if (location.pathname !== '/documents' && location.pathname !== '/chat') {
      navigate('/documents');
    }
  };

  return (
    <div className="app-layout">
      {/* Header */}
      <header className="app-header">
        {/* Status pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: 'var(--accent-green)',
            boxShadow: '0 0 6px var(--accent-green)',
            display: 'inline-block',
            animation: 'pulse-dot 2s ease-in-out infinite',
          }} />
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            {WORKER_URL.replace('https://', '').replace('http://', '')}
          </span>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Tenant Name and Plan */}
          <div 
            style={{ 
              background: 'rgba(124, 58, 237, 0.15)',
              border: '1px solid rgba(124, 58, 237, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              borderRadius: '20px',
              color: '#ffffff',
              fontWeight: 600,
              boxShadow: '0 0 10px rgba(124, 58, 237, 0.2)'
            }}
          >
            <span style={{ fontSize: '14px' }}>🏢</span>
            <span style={{ fontSize: '13px' }}>
              {tenant?.name || 'Tenant'}
            </span>
            {tenant?.role === 'admin' && (
              <span className="badge badge--purple" style={{ fontSize: '10px', padding: '1px 6px', color: '#ffffff', background: 'var(--accent-primary)', border: '1px solid #ffffff55' }}>
                Admin
              </span>
            )}
            <span className={`badge badge--${tenant?.plan === 'enterprise' ? 'purple' : tenant?.plan === 'pro' ? 'blue' : 'gray'}`} style={{ fontSize: '10px', padding: '1px 6px', color: '#ffffff' }}>
              {tenant?.plan || 'free'}
            </span>
          </div>

          <button className="btn btn--secondary btn--sm" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className="app-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: '10px', position: 'relative' }}>
        
        {/* Brand Header */}
        <div className="nav-logo" style={{ marginBottom: '4px', padding: '4px 6px 12px' }}>
          <div className="nav-logo__symbol">📄</div>
          <div>
            <span className="nav-logo__name">ErixOne</span>
            <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '1px' }}>DIaaS Core Engine</div>
          </div>
        </div>

        {/* ── KHO BÁU QUICK SWITCHER (Enterprise Workspace Selector) ── */}
        <div ref={vaultDropdownRef} style={{ position: 'relative', padding: '0 4px', marginBottom: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', padding: '0 2px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>💎</span> KHO BÁU HIỆN TẠI
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleQuickCreateVault();
              }}
              title="Tạo Kho báu mới"
              style={{
                background: 'rgba(56, 189, 248, 0.12)',
                border: '1px solid rgba(56, 189, 248, 0.35)',
                color: '#38bdf8',
                borderRadius: '5px',
                padding: '2px 7px',
                fontSize: '11px',
                cursor: 'pointer',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                transition: 'all 0.15s'
              }}
            >
              <span>+</span> Mới
            </button>
          </div>

          {/* Active Vault Pill */}
          <div
            onClick={() => {
              setShowVaultDropdown(prev => !prev);
              if (!showVaultDropdown) setVaultSearch('');
            }}
            style={{
              background: showVaultDropdown ? 'rgba(56, 189, 248, 0.15)' : 'rgba(15, 23, 42, 0.85)',
              border: `1px solid ${showVaultDropdown ? 'rgba(56, 189, 248, 0.5)' : 'rgba(148, 163, 184, 0.2)'}`,
              borderRadius: '8px',
              padding: '7px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              transition: 'all 0.2s',
              userSelect: 'none'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: '15px', flexShrink: 0 }}>{activeVaultInfo.icon}</span>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {activeVaultInfo.name}
                </span>
                <span style={{ fontSize: '9.5px', color: '#94a3b8', marginTop: '1px' }}>
                  {activeVaultInfo.typeLabel}
                </span>
              </div>
            </div>
            <span style={{ color: '#94a3b8', fontSize: '10px', transform: showVaultDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', marginLeft: '6px' }}>
              ▼
            </span>
          </div>

          {/* Interactive Popover Dropdown */}
          {showVaultDropdown && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: '4px',
                right: '4px',
                background: '#0f172a',
                border: '1px solid rgba(56, 189, 248, 0.4)',
                borderRadius: '10px',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.75)',
                zIndex: 1000,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Search Box */}
              <div style={{ padding: '8px', borderBottom: '1px solid rgba(148, 163, 184, 0.15)', background: 'rgba(15, 23, 42, 0.95)' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <span style={{ position: 'absolute', left: '8px', fontSize: '12px', color: '#64748b' }}>🔍</span>
                  <input
                    type="text"
                    placeholder="Tìm Kho báu..."
                    value={vaultSearch}
                    onChange={e => setVaultSearch(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '5px 8px 5px 26px',
                      borderRadius: '5px',
                      border: '1px solid rgba(56, 189, 248, 0.25)',
                      background: '#1e293b',
                      color: '#f8fafc',
                      fontSize: '11.5px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  {vaultSearch && (
                    <span
                      onClick={() => setVaultSearch('')}
                      style={{ position: 'absolute', right: '8px', fontSize: '11px', color: '#94a3b8', cursor: 'pointer' }}
                    >
                      ✕
                    </span>
                  )}
                </div>
              </div>

              {/* Scrollable list */}
              <div style={{ maxHeight: '250px', overflowY: 'auto', padding: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                
                {/* 1. Kho báu Cá nhân */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', padding: '4px 6px 2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    👤 Kho báu Cá nhân
                  </div>
                  
                  {/* Root / Default Personal */}
                  <div
                    onClick={() => handleSelectVault(null)}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '5px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      background: (activeVaultId === null || activeVaultId === '') ? 'rgba(56, 189, 248, 0.18)' : 'transparent',
                      color: (activeVaultId === null || activeVaultId === '') ? '#38bdf8' : '#e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontWeight: (activeVaultId === null || activeVaultId === '') ? 600 : 400
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🏠</span>
                      <span>Không gian của tôi (Gốc)</span>
                    </div>
                    {(activeVaultId === null || activeVaultId === '') && <span style={{ fontSize: '11px' }}>✓</span>}
                  </div>

                  {personalVaults.map(v => {
                    const isSelected = activeVaultId === v.id;
                    return (
                      <div
                        key={v.id}
                        onClick={() => handleSelectVault(v.id)}
                        style={{
                          padding: '5px 8px',
                          borderRadius: '5px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          background: isSelected ? 'rgba(56, 189, 248, 0.18)' : 'transparent',
                          color: isSelected ? '#38bdf8' : '#cbd5e1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          <span>📁</span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: '4px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const newName = prompt('Nhập tên mới cho Kho báu Cá nhân:', v.name);
                              if (newName && newName.trim() && newName.trim() !== v.name) {
                                updateVault(v.id, newName.trim());
                              }
                            }}
                            title="Đổi tên Kho báu"
                            style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '10px', padding: '1px 3px' }}
                          >✏️</button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Bạn có chắc chắn muốn xóa Kho báu "${v.name}"? Toàn bộ tài liệu bên trong sẽ bị xóa.`)) {
                                deleteVault(v.id);
                              }
                            }}
                            title="Xóa Kho báu"
                            style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '10px', padding: '1px 3px' }}
                          >🗑</button>
                          {isSelected && <span style={{ fontSize: '11px', color: '#38bdf8', marginLeft: '4px' }}>✓</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* 2. Kho báu Được chia sẻ */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', padding: '4px 6px 2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    🤝 Kho báu Được chia sẻ
                  </div>

                  <div
                    onClick={() => handleSelectVault('shared')}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '5px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      background: activeVaultId === 'shared' ? 'rgba(56, 189, 248, 0.18)' : 'transparent',
                      color: activeVaultId === 'shared' ? '#38bdf8' : '#e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontWeight: activeVaultId === 'shared' ? 600 : 400
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>📥</span>
                      <span>Hộp thư nhận (Inbox)</span>
                    </div>
                    {activeVaultId === 'shared' && <span style={{ fontSize: '11px' }}>✓</span>}
                  </div>

                  {sharedVaults.map(v => {
                    const isSelected = activeVaultId === v.id;
                    return (
                      <div
                        key={v.id}
                        onClick={() => handleSelectVault(v.id)}
                        style={{
                          padding: '5px 8px',
                          borderRadius: '5px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          background: isSelected ? 'rgba(56, 189, 248, 0.18)' : 'transparent',
                          color: isSelected ? '#38bdf8' : '#cbd5e1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          <span>📎</span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</span>
                        </div>
                        {isSelected && <span style={{ fontSize: '11px', color: '#38bdf8' }}>✓</span>}
                      </div>
                    );
                  })}
                </div>

                {/* 3. Kho báu Doanh nghiệp & Nhóm */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', padding: '4px 6px 2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    🏢 Kho báu Doanh nghiệp & Nhóm
                  </div>

                  {businessVaults.length === 0 ? (
                    <div style={{ fontSize: '11px', color: '#64748b', padding: '4px 8px', fontStyle: 'italic' }}>
                      Chưa có Kho báu nhóm nào
                    </div>
                  ) : (
                    businessVaults.map(v => {
                      const isSelected = activeVaultId === v.id;
                      return (
                        <div
                          key={v.id}
                          onClick={() => handleSelectVault(v.id)}
                          style={{
                            padding: '5px 8px',
                            borderRadius: '5px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            background: isSelected ? 'rgba(56, 189, 248, 0.18)' : 'transparent',
                            color: isSelected ? '#38bdf8' : '#cbd5e1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            <span>📁</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: '4px' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowVaultDropdown(false);
                                setVaultConfig(v);
                              }}
                              title="Phân quyền Kho báu này"
                              style={{ background: 'transparent', border: 'none', color: '#c084fc', cursor: 'pointer', fontSize: '11px', padding: '1px 3px' }}
                            >⚙️</button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const newName = prompt('Nhập tên mới cho Kho báu Doanh nghiệp:', v.name);
                                if (newName && newName.trim() && newName.trim() !== v.name) {
                                  updateVault(v.id, newName.trim());
                                }
                              }}
                              title="Đổi tên Kho báu"
                              style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '10px', padding: '1px 3px' }}
                            >✏️</button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm(`Bạn có chắc chắn muốn xóa Kho báu "${v.name}"? Toàn bộ tài liệu bên trong sẽ bị xóa.`)) {
                                  deleteVault(v.id);
                                }
                              }}
                              title="Xóa Kho báu"
                              style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '10px', padding: '1px 3px' }}
                            >🗑</button>
                            {isSelected && <span style={{ fontSize: '11px', color: '#38bdf8', marginLeft: '4px' }}>✓</span>}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

              </div>

              {/* Popover Footer Button */}
              <div style={{ padding: '8px', borderTop: '1px solid rgba(148, 163, 184, 0.15)', background: 'rgba(15, 23, 42, 0.95)' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowVaultDropdown(false);
                    handleQuickCreateVault();
                  }}
                  style={{
                    width: '100%',
                    padding: '6px',
                    borderRadius: '6px',
                    background: 'linear-gradient(135deg, rgba(6,182,212,0.2) 0%, rgba(59,130,246,0.2) 100%)',
                    border: '1px solid rgba(56, 189, 248, 0.4)',
                    color: '#38bdf8',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <span>➕</span> Tạo Kho báu mới
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── SCROLLABLE NAVIGATION AREA ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', flex: 1, overflowY: 'auto', paddingRight: '2px' }}>
          
          {/* GROUP 1: TRUNG TÂM TÁC VỤ (Core Workspace) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div className="nav-section__label" style={{ padding: '0 8px', color: '#64748b', fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px' }}>
              TRUNG TÂM TÁC VỤ
            </div>

            {/* RAG Chat & Conversations */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <NavLink
                  to="/chat"
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: '7px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '13px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    transition: 'all 0.15s'
                  }}
                >
                  <span className="nav-item__icon">💬</span>
                  <span>Hỏi Đáp & Trò Chuyện AI</span>
                </NavLink>
                <button
                  onClick={() => {
                    createConversation([]);
                    navigate('/chat');
                  }}
                  title="Tạo cuộc trò chuyện mới"
                  style={{
                    background: 'rgba(6, 182, 212, 0.15)',
                    border: '1px solid rgba(6, 182, 212, 0.35)',
                    color: '#38bdf8',
                    borderRadius: '6px',
                    padding: '6px 8px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    transition: 'all 0.15s'
                  }}
                >
                  +
                </button>
              </div>

              {/* Collapsible recent chats */}
              {conversations.length > 0 && (
                <div style={{ marginLeft: '12px', borderLeft: '2px solid rgba(56, 189, 248, 0.25)', paddingLeft: '8px', marginTop: '2px' }}>
                  <div 
                    onClick={() => setShowRecentChats(prev => !prev)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 4px', cursor: 'pointer', fontSize: '10.5px', color: '#94a3b8' }}
                  >
                    <span>Lịch sử gần đây ({conversations.length})</span>
                    <span style={{ fontSize: '9px' }}>{showRecentChats ? '▲' : '▼'}</span>
                  </div>
                  {showRecentChats && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '140px', overflowY: 'auto' }}>
                      {conversations.slice(0, 5).map(c => {
                        const isActive = activeId === c.id;
                        return (
                          <div
                            key={c.id}
                            onClick={() => {
                              setActiveId(c.id);
                              navigate('/chat');
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '4px 6px',
                              borderRadius: '4px',
                              fontSize: '11.5px',
                              color: isActive ? '#38bdf8' : 'var(--text-secondary)',
                              background: isActive ? 'rgba(6,182,212,0.12)' : 'transparent',
                              cursor: 'pointer',
                              overflow: 'hidden'
                            }}
                          >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, paddingRight: '4px' }}>
                              💬 {c.title}
                            </span>
                            <button
                              title="Xóa đoạn chat"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm('Xoá đoạn chat này?')) {
                                  deleteConversation(c.id);
                                }
                              }}
                              style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '10px', padding: '0 2px' }}
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Documents Library */}
            <NavLink
              to="/documents"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px',
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '13px',
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'all 0.15s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className="nav-item__icon">📁</span>
                <span>Kho Tài Liệu</span>
              </div>
              <span style={{ fontSize: '9.5px', background: 'rgba(56, 189, 248, 0.18)', color: '#38bdf8', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                Song Đĩa
              </span>
            </NavLink>

            {/* Extractors */}
            <NavLink
              to="/extractors"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px',
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '13px',
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">⚡</span>
              <span>Bóc Tách Dữ Liệu</span>
            </NavLink>

            {/* Dashboard */}
            <NavLink
              to="/dashboard"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px',
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '13px',
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">📊</span>
              <span>Bảng Điều Khiển</span>
            </NavLink>
          </div>

          {/* GROUP 2: TRÍ TUỆ KHO BÁU (Vault Intelligence & Personal) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div className="nav-section__label" style={{ padding: '0 8px', color: '#64748b', fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px' }}>
              TRÍ TUỆ KHO BÁU
            </div>

            <NavLink
              to="/profile"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">👤</span>
              <span>Hồ Sơ Kho Báu</span>
            </NavLink>

            <NavLink
              to="/bots"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">🤖</span>
              <span>Trợ Lý AI Kho Báu</span>
            </NavLink>

            <NavLink
              to="/growth"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">📈</span>
              <span>Nhật Ký Tăng Trưởng</span>
            </NavLink>

            <NavLink
              to="/training"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">🧠</span>
              <span>Huấn Luyện Flashcard</span>
            </NavLink>

            <NavLink
              to="/operations"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">⚙️</span>
              <span>Trạm Vận Hành (Hub)</span>
            </NavLink>
          </div>

          {/* GROUP 3: DOANH NGHIỆP & HỆ SINH THÁI */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div className="nav-section__label" style={{ padding: '0 8px', color: '#64748b', fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px' }}>
              DOANH NGHIỆP & HỆ SINH THÁI
            </div>

            <NavLink
              to="/workspace"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">🏢</span>
              <span>Không Gian Doanh Nghiệp</span>
            </NavLink>

            <NavLink
              to="/graph"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">🕸️</span>
              <span>Sơ Đồ Tri Thức 2D</span>
            </NavLink>

            <NavLink
              to="/collective"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">🌐</span>
              <span>Dòng Tin Tập Thể</span>
            </NavLink>

            <NavLink
              to="/agents"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">🔌</span>
              <span>Cổng API & MCP Server</span>
            </NavLink>
          </div>

          {/* GROUP 4: KINH TẾ TRI THỨC & DI SẢN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div className="nav-section__label" style={{ padding: '0 8px', color: '#64748b', fontSize: '10px', fontWeight: 700, letterSpacing: '0.8px' }}>
              KINH TẾ TRI THỨC & DI SẢN
            </div>

            <NavLink
              to="/expertise"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">🏆</span>
              <span>Thẩm Định Năng Lực</span>
            </NavLink>

            <NavLink
              to="/marketplace"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">🏪</span>
              <span>Chợ Kỹ Năng Skill-Pack</span>
            </NavLink>

            <NavLink
              to="/legacy"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">📜</span>
              <span>Di Chúc Tri Thức Số</span>
            </NavLink>
          </div>

          {/* GROUP 5: ĐỘI NGŨ & QUẢN TRỊ (Pinned Bottom) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
            <NavLink
              to="/team"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">👥</span>
              <span>Đội Ngũ (My Team)</span>
            </NavLink>

            <NavLink
              to="/billing"
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              style={{
                padding: '8px 10px', borderRadius: '7px', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                textDecoration: 'none', transition: 'all 0.15s'
              }}
            >
              <span className="nav-item__icon">💳</span>
              <span>Gói Cước & Sử Dụng</span>
            </NavLink>

            {tenant?.role === 'admin' && (
              <>
                <div className="nav-section__label" style={{ padding: '6px 8px 2px', marginTop: '4px', color: 'var(--accent-purple)', fontSize: '10px', fontWeight: 700 }}>
                  QUẢN TRỊ VIÊN (ADMIN)
                </div>
                <NavLink
                  to="/admin/plans"
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  style={{
                    padding: '8px 10px', borderRadius: '7px', display: 'flex',
                    alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                    textDecoration: 'none', transition: 'all 0.15s'
                  }}
                >
                  <span className="nav-item__icon">📋</span>
                  <span>Billing Plans</span>
                </NavLink>
                <NavLink
                  to="/admin/users"
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  style={{
                    padding: '8px 10px', borderRadius: '7px', display: 'flex',
                    alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                    textDecoration: 'none', transition: 'all 0.15s'
                  }}
                >
                  <span className="nav-item__icon">👥</span>
                  <span>Users & Quota</span>
                </NavLink>
                <NavLink
                  to="/admin/voice"
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  style={{
                    padding: '8px 10px', borderRadius: '7px', display: 'flex',
                    alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                    textDecoration: 'none', transition: 'all 0.15s'
                  }}
                >
                  <span className="nav-item__icon">🎙️</span>
                  <span>Erix Voice Config</span>
                </NavLink>
                <NavLink
                  to="/admin/ai-proxy"
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  style={{
                    padding: '8px 10px', borderRadius: '7px', display: 'flex',
                    alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 500,
                    textDecoration: 'none', transition: 'all 0.15s'
                  }}
                >
                  <span className="nav-item__icon">🔀</span>
                  <span>Cổng Erix AI Proxy</span>
                </NavLink>
              </>
            )}
          </div>

        </div>
      </aside>

      {/* Main content */}
      <main className="app-content">
        <Outlet />
      </main>

      {vaultConfig && (
        <VaultAccessModal vault={vaultConfig} onClose={() => setVaultConfig(null)} />
      )}
    </div>
  );
}
